import { TechCrew } from 'src/project/types/techCrews';
import { ConrepAppUserStatus, ConrepAppWorkday } from '../types';
import { TechCrewPermission } from 'src/project/types/techCrews';
import { GetProjectResponse } from 'src/project/types/projects';
import { isProjectOwnerOrAdmin } from 'src/user/utils';

export function isActionAllowed(
  conrepCurrentUserStatus: ConrepAppUserStatus | null,
  conrepWorkdayData: ConrepAppWorkday | null,
) {
  return !conrepCurrentUserStatus && !conrepWorkdayData?.isLocked;
}

function hasTechCrewPermission(
  userId: string,
  techCrew: TechCrew,
  permission: TechCrewPermission,
) {
  const isAdmin = techCrew.admins.includes(userId);
  const memberEntry = techCrew.members.find((m) => m.userId === userId);
  const hasPermission = !!memberEntry?.permissions.includes(permission);
  return isAdmin || hasPermission;
}

export function hasExactlyTechCrewPermission(
  userId: string,
  techCrew: TechCrew,
  permission: TechCrewPermission,
) {
  const memberEntry = techCrew.members.find((m) => m.userId === userId);
  const hasPermission = !!memberEntry?.permissions.includes(permission);
  return hasPermission;
}

export function hasViewDocumentationPermission(
  userId: string,
  techCrew: TechCrew,
) {
  return hasTechCrewPermission(
    userId,
    techCrew,
    TechCrewPermission.VIEW_DOCUMENTATION,
  );
}

export function hasEditDocumentationPermission(
  userId: string,
  techCrew: TechCrew,
) {
  return hasTechCrewPermission(
    userId,
    techCrew,
    TechCrewPermission.EDIT_DOCUMENTATION,
  );
}

export function hasControlDocumentationPermission(
  userId: string,
  techCrew: TechCrew,
) {
  return hasTechCrewPermission(
    userId,
    techCrew,
    TechCrewPermission.CONTROL_DOCUMENTATION,
  );
}

export function hasExportDocumentationDataPermission(
  userId: string,
  techCrew: TechCrew,
) {
  return hasTechCrewPermission(
    userId,
    techCrew,
    TechCrewPermission.EXPORT_DATA_DOCUMENTATION,
  );
}

export function hasViewDocumentsPermission(
  userId: string,
  techCrew: TechCrew,
  project: GetProjectResponse,
) {
  // First check if user is project owner or admin - they get automatic access
  if (isProjectOwnerOrAdmin(userId, project)) {
    return true;
  }

  // Otherwise, check tech crew specific permissions
  return hasTechCrewPermission(
    userId,
    techCrew,
    TechCrewPermission.VIEW_DOCUMENTS,
  );
}

export function hasViewAnalysisPermission(userId: string, techCrew: TechCrew) {
  return hasTechCrewPermission(
    userId,
    techCrew,
    TechCrewPermission.VIEW_ANALYSIS,
  );
}

export function hasEditAnalysisPermission(userId: string, techCrew: TechCrew) {
  return hasTechCrewPermission(
    userId,
    techCrew,
    TechCrewPermission.EDIT_ANALYSIS,
  );
}

export type ControllerDocumentationVisibility = {
  isControllerOnly: boolean;
  canViewAttendanceNamesWorktimeHours: boolean;
  canViewAttendanceNamesCompanyPeopleComment: boolean;
  canViewAttendancePeopleCount: boolean;
  canViewAttendanceTotalHours: boolean;
  canViewBoqPositions: boolean;
  canViewBoqPositionHours: boolean;
};

const FULL_CONTROLLER_DOCUMENTATION_VISIBILITY: ControllerDocumentationVisibility =
  {
    isControllerOnly: false,
    canViewAttendanceNamesWorktimeHours: true,
    canViewAttendanceNamesCompanyPeopleComment: true,
    canViewAttendancePeopleCount: true,
    canViewAttendanceTotalHours: true,
    canViewBoqPositions: true,
    canViewBoqPositionHours: true,
  };

export function isControllerOnlyUser(
  userId: string,
  project: GetProjectResponse,
  techCrew: TechCrew,
) {
  const isOwnerOrAdmin = isProjectOwnerOrAdmin(userId, project);
  const isTechCrewAdmin = techCrew.admins.includes(userId);
  const hasControllerPermission = hasExactlyTechCrewPermission(
    userId,
    techCrew,
    TechCrewPermission.CONTROL_DOCUMENTATION,
  );

  return hasControllerPermission && !isOwnerOrAdmin && !isTechCrewAdmin;
}

export function isControllerEditingBlocked(
  userId: string,
  project: GetProjectResponse,
  techCrew: TechCrew | null,
) {
  if (!techCrew) {
    return false;
  }

  return (
    isControllerOnlyUser(userId, project, techCrew) &&
    !hasEditDocumentationPermission(userId, techCrew)
  );
}

export function getControllerDocumentationVisibility(
  userId: string,
  project: GetProjectResponse,
  techCrew: TechCrew | null,
): ControllerDocumentationVisibility {
  if (!techCrew || !isControllerOnlyUser(userId, project, techCrew)) {
    return FULL_CONTROLLER_DOCUMENTATION_VISIBILITY;
  }

  if (!isControllerEditingBlocked(userId, project, techCrew)) {
    return FULL_CONTROLLER_DOCUMENTATION_VISIBILITY;
  }

  return {
    isControllerOnly: true,
    canViewAttendanceNamesWorktimeHours: hasExactlyTechCrewPermission(
      userId,
      techCrew,
      TechCrewPermission.VIEW_ATTENDANCE_NAMES_WORKTIME_HOURS,
    ),
    canViewAttendanceNamesCompanyPeopleComment: hasExactlyTechCrewPermission(
      userId,
      techCrew,
      TechCrewPermission.VIEW_ATTENDANCE_NAMES_COMPANY_PEOPLE_COMMENT,
    ),
    canViewAttendancePeopleCount: hasExactlyTechCrewPermission(
      userId,
      techCrew,
      TechCrewPermission.VIEW_ATTENDANCE_PEOPLE_COUNT,
    ),
    canViewAttendanceTotalHours: hasExactlyTechCrewPermission(
      userId,
      techCrew,
      TechCrewPermission.VIEW_ATTENDANCE_TOTAL_HOURS,
    ),
    canViewBoqPositions: hasExactlyTechCrewPermission(
      userId,
      techCrew,
      TechCrewPermission.VIEW_BOQ_POSITIONS_HOURS,
    ),
    canViewBoqPositionHours: hasExactlyTechCrewPermission(
      userId,
      techCrew,
      TechCrewPermission.VIEW_BOQ_POSITIONS_HOURS,
    ),
  };
}

export function hasManagePlanningPermission(
  userId: string,
  project: GetProjectResponse,
) {
  return (
    project.planningManagers.includes(userId) ||
    isProjectOwnerOrAdmin(userId, project)
  );
}
